import { executeCapability } from './capabilityExecute'

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

function getConfiguredVapidPublicKey(): string {
  return String(import.meta.env.VITE_VAPID_PUBLIC_KEY || '').trim()
}

export type WebPushPermission = NotificationPermission | 'unsupported'

export async function getWebPushPermission(): Promise<WebPushPermission> {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported'
  return Notification.permission
}

export function readWebPushPermission(): WebPushPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported'
  return Notification.permission
}

/** Missing or true on the server means opted in. */
export async function getPushNotificationsEnabled(): Promise<boolean> {
  const res = await executeCapability<{ enabled?: boolean }>({
    app: 'global',
    capability: 'push.getPreferences',
    input: {},
  })
  return res?.enabled !== false
}

export async function setPushNotificationsEnabled(enabled: boolean): Promise<void> {
  await executeCapability({
    app: 'global',
    capability: 'push.setEnabled',
    input: { enabled },
  })
}

async function unsubscribeLocalPushSubscriptions(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  const regs = await navigator.serviceWorker.getRegistrations()
  await Promise.all(
    regs.map(async (reg) => {
      try {
        const sub = await reg.pushManager.getSubscription()
        if (sub) await sub.unsubscribe()
      } catch {
        // ignore
      }
    })
  )
}

/** Persist opt-out, drop server endpoints, and unsubscribe this browser. */
export async function disableWebPush(): Promise<void> {
  await setPushNotificationsEnabled(false)
  await unsubscribeLocalPushSubscriptions()
}

/**
 * Ask the browser and subscribe this device, then keep the account opted in.
 * If this account was opted out, the flag is set only long enough for subscribe
 * to be accepted, and is restored when subscription fails.
 */
export async function enableWebPush(): Promise<{
  ok: boolean
  enabled: boolean
  reason?: string
  permission?: WebPushPermission
}> {
  const wasEnabled = await getPushNotificationsEnabled()
  if (!wasEnabled) {
    await setPushNotificationsEnabled(true)
  }

  const result = await ensureWebPushSubscription()
  if (result.ok) {
    return { ...result, enabled: true }
  }

  if (!wasEnabled) {
    try {
      await setPushNotificationsEnabled(false)
    } catch {
      return { ...result, enabled: true }
    }
    await unsubscribeLocalPushSubscriptions()
    return { ...result, enabled: false }
  }

  return { ...result, enabled: true }
}

/**
 * Request permission (if needed), subscribe via PushManager, and upsert on the server.
 * No-ops gracefully when SW/VAPID unavailable (e.g. development).
 */
export async function ensureWebPushSubscription(): Promise<{
  ok: boolean
  reason?: string
  permission?: WebPushPermission
}> {
  if (typeof window === 'undefined') return { ok: false, reason: 'Not in browser' }
  if (!window.isSecureContext) return { ok: false, reason: 'Secure context required' }
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, reason: 'Push not supported in this browser', permission: 'unsupported' }
  }

  let permission = Notification.permission
  if (permission === 'default') {
    permission = await Notification.requestPermission()
  }
  if (permission !== 'granted') {
    return { ok: false, reason: 'Notification permission not granted', permission }
  }

  const envKey = getConfiguredVapidPublicKey()
  let publicKey = envKey
  if (!publicKey) {
    try {
      const res = await executeCapability<{ publicKey: string }>({
        app: 'global',
        capability: 'push.vapidPublicKey',
        input: {},
      })
      publicKey = String(res?.publicKey || '').trim()
    } catch (err) {
      return {
        ok: false,
        reason: err instanceof Error ? err.message : 'VAPID public key unavailable',
        permission,
      }
    }
  }
  if (!publicKey) {
    return { ok: false, reason: 'VAPID public key not configured', permission }
  }

  try {
    const registration = await navigator.serviceWorker.ready
    let subscription = await registration.pushManager.getSubscription()
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      })
    }
    const json = subscription.toJSON()
    await executeCapability({
      app: 'global',
      capability: 'push.subscribe',
      input: {
        subscription: {
          endpoint: json.endpoint,
          keys: {
            p256dh: json.keys?.p256dh,
            auth: json.keys?.auth,
          },
        },
      },
    })
    return { ok: true, permission }
  } catch (err) {
    return {
      ok: false,
      reason: err instanceof Error ? err.message : 'Failed to subscribe',
      permission,
    }
  }
}

/** User ids already asked (or skipped) during this page load. Survives logout without a reload. */
const promptedUserIds = new Set<string>()

/**
 * After login: if this account is opted in and the browser has not decided yet, ask once.
 * Another account in the same tab is asked separately. A later click on the settings
 * toggle can ask again. Dismissal does not repeat for that same account until reload.
 */
export async function promptForWebPushIfDefault(userId: string): Promise<void> {
  const id = String(userId || '').trim()
  if (!id || promptedUserIds.has(id)) return
  if (typeof window === 'undefined' || !('Notification' in window)) return
  if (Notification.permission !== 'default') return
  promptedUserIds.add(id)
  try {
    const enabled = await getPushNotificationsEnabled()
    if (!enabled) return
    await ensureWebPushSubscription()
  } catch {
    // ignore
  }
}

/** Silent refresh when permission already granted. No-ops when the account has opted out. */
export async function refreshWebPushSubscriptionIfGranted(): Promise<void> {
  if (typeof window === 'undefined') return
  if (!('Notification' in window)) return
  if (Notification.permission !== 'granted') return
  if (!('serviceWorker' in navigator)) return
  try {
    const enabled = await getPushNotificationsEnabled()
    if (!enabled) return
    const regs = await navigator.serviceWorker.getRegistrations()
    if (!regs.length) return
    await ensureWebPushSubscription()
  } catch {
    // ignore
  }
}
